import React, {
  isValidElement,
  cloneElement,
  Children,
  useState,
  memo,
} from "react";
import _ from "lodash";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import classNames from "classnames";

// design system components
import { Popover } from "@toddle-edu/react-tiny-popover";
import { IconButton } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  MoveArrowsOutlined,
  AddCircleOutlined,
} from "@toddle-edu/ds-icons";

// utils and constants
import { getColumnId, getRowId } from "../../../../modules/Utils";
import {
  SCORE_BASED_RUBRIC_ROW,
  SCORE_BASED_RUBRIC_COLUMN,
} from "../../../../modules/Constants";
import { useIntersectionObserver } from "Utils/UseIntersectionObserver";

// scss
import classes from "./DragAndDropHelpers.scss";

// common styles configuration
const styles = {
  rowPopoverStyles: {
    zIndex: 1100,
    left: "-4px",
  },
  columnPopoverStyles: {
    zIndex: 1100,
    top: "10px",
  },
};

/**
 * toggle visibility of an element by adding/removing a css class
 * @param {Object} params - parameters object
 * @param {string} params.elementId - id of the element to toggle
 * @param {string} params.action - action to perform ('add' or 'remove')
 */
const toggleVisibility = ({ elementId, action }) => {
  if (elementId) {
    const element = document.getElementById(elementId);
    element?.classList?.[action](classes.visible);
  }
};

/**
 * handle mouse over events for row and column elements
 * @param {Object} params - parameters object
 * @param {string} params.rowId - id of the row element
 * @param {string} params.columnId - id of the column element
 */
export const onMouseOver = ({ rowId, columnId }) => {
  if (rowId) {
    toggleVisibility({ elementId: getRowId(rowId), action: "add" });
  }
  if (columnId) {
    toggleVisibility({ elementId: getColumnId(columnId), action: "add" });
  }
};

/**
 * handle mouse leave events for row and column elements
 * @param {Object} params - parameters object
 * @param {string} params.rowId - id of the row element
 * @param {string} params.columnId - id of the column element
 */
export const onMouseLeave = ({ rowId, columnId }) => {
  if (rowId) {
    toggleVisibility({ elementId: getRowId(rowId), action: "remove" });
  }
  if (columnId) {
    toggleVisibility({ elementId: getColumnId(columnId), action: "remove" });
  }
};

/**
 * handle focus events for row and column elements - mirrors mouse over behavior
 */
export const onFocus = ({ rowId, columnId }) => {
  onMouseOver({ rowId, columnId });
};

/**
 * handle blur events for row and column elements - mirrors mouse leave behavior
 */
export const onBlur = ({ rowId, columnId }) => {
  onMouseLeave({ rowId, columnId });
};

export const SortableRow = memo(props => {
  const {
    testId,
    criterion,
    children,
    showPopover,
    onClickDeleteCriterion,
    labelTextAreaRefs,
    onAddCriterion,
    isValid,
    showTotalPoints = false,
  } = props;

  const id = criterion.id;
  const popoverContainerId = getRowId(id);
  const disabled = _.includes(id, "NEW_") || !isValid;

  // initialize sortable functionality
  const {
    node,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // viewport detection
  const isInViewport = useIntersectionObserver(node, {
    threshold: 0.1,
    rootMargin: "50px",
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    zIndex: isDragging ? 1100 : "auto",
    position: isDragging ? "relative" : undefined,
  };

  // event handlers
  const _onMouseOver = e => {
    if (isDragging) return;
    e.stopPropagation();
    onMouseOver({ rowId: id });
  };

  const _onMouseLeave = () => {
    if (isDragging) return;
    onMouseLeave({ rowId: id });
  };

  const _onFocus = () => {
    if (isDragging) return;
    onFocus({ rowId: id });
  };

  const _onBlur = () => {
    if (isDragging) return;
    onBlur({ rowId: id });
  };

  // move to prev row, add line (last cell)
  const onAddButtonKeyDown = e => {
    const { key } = e;
    const { items, sortButtonRef } = props;

    if (key === "Tab" && e.shiftKey) {
      const prevItemIndex = _.findIndex(items, item => item?.id === id) - 1;
      const prevItem = items[prevItemIndex]?.id;

      if (prevItemIndex !== -1) {
        const addLine = document.querySelector(`[id*="ADD_LINE_${prevItem}"]`);

        if (addLine) {
          e.preventDefault();
          addLine.focus();
        }
      } else {
        setTimeout(() => {
          sortButtonRef?.current?.focus();
        }, 10);
      }
    }
  };

  // Combine the onKeyDown from listeners with our custom onMoveButtonKeyDown
  const handleCombinedMoveKeyDown = e => {
    if (_.isFunction(listeners.onKeyDown)) {
      listeners.onKeyDown(e);
    }

    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      labelTextAreaRefs?.current[id]?.focus();
    }
  };

  // clone children with additional props
  const childrenWithProps = Children.map(children, child => {
    if (isValidElement(child)) return cloneElement(child, { isDragging });
    return child;
  });

  const cursorClass = classNames({
    [classes.cursorGrab]: true,
    [classes.cursorGrabbing]: isDragging,
  });

  const shouldShowPopover =
    !disabled && node?.current && showPopover && isInViewport;

  const rowClassName = classNames(classes.row, {
    [classes.rowWithTotalPoints]: showTotalPoints,
  });

  return (
    <div
      id={`${SCORE_BASED_RUBRIC_ROW}_${id}`}
      className={rowClassName}
      style={style}
      onMouseOver={_onMouseOver}
      onMouseLeave={_onMouseLeave}
      onFocus={_onFocus}
      onBlur={_onBlur}
      ref={setNodeRef}
    >
      {shouldShowPopover ? (
        <Popover
          isOpen={true}
          align="center"
          positions={["left"]}
          containerStyle={styles.rowPopoverStyles}
          boundaryInset={20}
          reposition={true}
          content={() => (
            <div
              id={popoverContainerId}
              className={classes.popoverRowIconsContainer}
            >
              <IconButton
                icon={<AddCircleOutlined />}
                size="x-small"
                variant="neutral"
                type="plain"
                onClick={() => onAddCriterion(id)}
                onKeyDown={onAddButtonKeyDown}
                testId={`${testId}-row-${id}-add-iconButton`}
              />

              <IconButton
                icon={<DeleteOutlined />}
                size="x-small"
                variant="neutral"
                type="plain"
                onClick={() => onClickDeleteCriterion(id)}
                testId={`${testId}-row-${id}-delete-iconButton`}
              />
              <IconButton
                icon={<MoveArrowsOutlined />}
                size="x-small"
                variant="neutral"
                type="plain"
                className={cursorClass}
                {...listeners}
                {...attributes}
                onKeyDown={handleCombinedMoveKeyDown}
                testId={`${testId}-row-${id}move-iconButton`}
              />
            </div>
          )}
          childrenRef={{ current: node.current }}
          isChildrenRefPassed={true}
        />
      ) : null}

      {childrenWithProps}
    </div>
  );
});

SortableRow.displayName = "SortableRow";

export const SortableDescriptor = memo(props => {
  const {
    testId,
    descriptor,
    rowId,
    children,
    showPopover,
    onClickDeleteDescriptor,
    labelTextAreaRefs,
    onAddDescriptor,
    isValid,
    isStandardBasedRubric,
    allowGradeScaleValueSelection,
    showAddIcon = true,
  } = props;

  const id = descriptor.id;
  const popoverContainerId = getColumnId(id);
  const disabled = _.includes(id, "NEW_") || !isValid;

  const [isRefSet, setIsRefSet] = useState(false);

  // initialize sortable functionality
  const {
    node,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // viewport detection
  const isInViewport = useIntersectionObserver(node, {
    threshold: 0.1,
    rootMargin: "50px",
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    zIndex: isDragging ? 1100 : "auto",
    position: isDragging ? "relative" : undefined,
  };

  // event handlers
  const _onMouseOver = e => {
    if (isDragging) return;
    e.stopPropagation();
    onMouseOver({ rowId, columnId: id });
  };

  const _onMouseLeave = () => {
    if (isDragging) return;
    onMouseLeave({ rowId, columnId: id });
  };

  const _onFocus = () => {
    if (isDragging) return;
    onFocus({ rowId, columnId: id });
  };

  const _onBlur = () => {
    if (isDragging) return;
    onBlur({ rowId, columnId: id });
  };

  // clone children with additional props
  const childrenWithProps = Children.map(children, child => {
    if (isValidElement(child)) return cloneElement(child, { isDragging });
    return child;
  });

  const cursorClass = classNames({
    [classes.cursorGrab]: true,
    [classes.cursorGrabbing]: isDragging,
  });

  const setRef = ref => {
    setNodeRef(ref);
    setIsRefSet(!!ref);
  };

  const shouldShowPopover =
    !disabled &&
    (node?.current || isRefSet) &&
    showPopover &&
    isInViewport &&
    (!isStandardBasedRubric || allowGradeScaleValueSelection);

  // move to prev cell add line
  const onAddButtonKeyDown = e => {
    const { key } = e;
    const { items } = props;

    if (key === "Tab" && e.shiftKey) {
      const prevItemIndex = _.findIndex(items, item => item.id === id) - 1;

      let prevItem = items[prevItemIndex]?.id;

      if (prevItemIndex < 0) {
        prevItem = rowId;
      }

      const addLine = document.querySelector(`[id*="ADD_LINE_${prevItem}"]`);
      if (addLine) {
        e.preventDefault();
        addLine.focus();
      }
    }
  };

  // Combine the onKeyDown from listeners with our custom onMoveButtonKeyDown
  const handleCombinedMoveKeyDown = e => {
    if (_.isFunction(listeners?.onKeyDown)) {
      listeners.onKeyDown(e);
    }

    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      labelTextAreaRefs?.current[id]?.focus();
    }
  };

  return (
    <div
      id={`${SCORE_BASED_RUBRIC_COLUMN}_${id}`}
      style={style}
      ref={setRef}
      className={classes.childrenContainer}
      onMouseOver={_onMouseOver}
      onMouseLeave={_onMouseLeave}
      onFocus={_onFocus}
      onBlur={_onBlur}
    >
      {shouldShowPopover ? (
        <Popover
          isOpen={true}
          align="center"
          positions={["top"]}
          containerStyle={styles.columnPopoverStyles}
          boundaryInset={20}
          reposition={true}
          content={() => (
            <div
              id={popoverContainerId}
              className={classes.popoverColumnIconsContainer}
            >
              {showAddIcon ? (
                <IconButton
                  icon={<AddCircleOutlined />}
                  size="x-small"
                  variant="neutral"
                  type="plain"
                  onClick={() => onAddDescriptor(id)}
                  onKeyDown={onAddButtonKeyDown}
                  testId={`${testId}-row-${id}-add-iconButton`}
                />
              ) : null}

              <IconButton
                icon={<DeleteOutlined />}
                size="x-small"
                variant="neutral"
                type="plain"
                onClick={() => onClickDeleteDescriptor(id)}
                testId={`${testId}-column-${id}-delete-iconButton`}
              />
              <IconButton
                icon={<MoveArrowsOutlined />}
                size="x-small"
                variant="neutral"
                type="plain"
                className={cursorClass}
                {...attributes}
                {...listeners}
                onKeyDown={handleCombinedMoveKeyDown}
                testId={`${testId}-column-${id}-move-iconButton`}
              />
            </div>
          )}
          childrenRef={{ current: node.current }}
          isChildrenRefPassed={true}
        />
      ) : null}

      {childrenWithProps}
    </div>
  );
});

SortableDescriptor.displayName = "SortableDescriptor";
